import { KatAppLayout, KatButton, KatNavigation, KatPopover } from '@amzn/katal-react';
import React from 'react';
import { LocalizationContextBuilder } from '@amzn/arb-tools';
import { applicationNavigationRoutes } from 'src/core/hooks/useNavigationRoutes';
import { useUser } from 'src/utils/UserContext';
import { DEFAULT_LOCALE } from '../constants';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { CORA_PATH, SHIPMENT_HISTORY_PATH, TRB_PATH } from 'src/core/config/navigationRoutes';
import arbManifest from 'translations/arbManifest';
import { resolveBundleUrlWithFallbacks } from '../helpers';
import { getEnv } from 'src/utils/Environment';
import { ENV } from 'src/core/config/APIConstants';
import { TRB_REGIONS } from 'src/features/types/trbTypes';
import { useTRBSnapshots } from 'src/features/hooks/useTRBData';

const mbmOptions = {
    arbManifest,
    defaultLocalizationContext: new LocalizationContextBuilder().withLocale( DEFAULT_LOCALE ).withDefaultLocale( DEFAULT_LOCALE ).build(),
    resolveBundleUrl: (
        resourcePackPath: string | undefined,
        locale: string,
        bundle: string,
    ) => resolveBundleUrlWithFallbacks( resourcePackPath, locale, bundle ),
};

import ErrorBoundary from './ErrorBoundary';
import { memo } from 'react';

const App = memo(() =>
{

    const { granularSLALanding, granularSLARoute, bulkGranularSLARoute, r2fThresholdRoute, bulkR2fThresholdRoute, coraRoute, trbRoute, shipmentHistoryRoute, fcConfigRoute } = applicationNavigationRoutes();
    const { user } = useUser();
    const location = useLocation();
    const isCORAPage = location.pathname === CORA_PATH || location.pathname.startsWith( `${CORA_PATH}/` ) || location.pathname.endsWith( '/cora' );
    const isTRBPage = location.pathname === TRB_PATH || location.pathname.startsWith( `${TRB_PATH}/` );
    const isShipmentHistoryPage = location.pathname === SHIPMENT_HISTORY_PATH || location.pathname.startsWith( `${SHIPMENT_HISTORY_PATH}/` );

    // TRB-specific data for header elements (only fetched on TRB pages)
    const { data: snapshotUrls } = useTRBSnapshots( isTRBPage );

    // Region is encoded in the hostname subdomain (e.g. prod.zaz.newton.aft.amazon.dev),
    // not in the URL path. Derive the current region from the subdomain so the header
    // dropdown reflects the region the user is actually viewing.
    const regionToSubdomain: Record<string, string> = {
        'NA': 'na',
        'ZAZ': 'zaz',
        'FE': 'nrt',
    };
    const subdomainToRegion: Record<string, string> = {};
    Object.keys( regionToSubdomain ).forEach( ( region ) => {
        subdomainToRegion[regionToSubdomain[region]] = region;
    } );
    const hostnameSubdomain = window.location.hostname.split( '.' )[1];
    const currentTRBRegion = subdomainToRegion[hostnameSubdomain] ?? 'NA';

    const handleTRBRegionChange = ( newRegion: string ) => {
        const subdomain = regionToSubdomain[newRegion] || 'na';
        const { protocol, hostname } = window.location;
        const parts = hostname.split( '.' );
        if ( parts.length >= 2 ) {
            parts[1] = subdomain;
            window.location.href = `${protocol}//${parts.join( '.' )}/trb`;
        }
    };

    const permissionsAvailable = () =>
    {
        return <ul style={{ listStyle: 'none', padding: 0 }}>
            {user?.isAuthorizedForSLAOverrides && (
                <li style={{ marginBottom: '0.5rem' }}>
                    SLA Override Access
                </li>
            )}
            {user?.isDevAdmin && (
                <li style={{ marginBottom: '0.5rem' }}>
                    Developer Access
                </li>
            )}
            {user?.ppgAdmin && (
                <li style={{ marginBottom: '0.5rem' }}>
                    PPG Admin Access
                </li>
            )}
        </ul>;
    };


    return (
            <KatAppLayout>
            <div className={'navigationHeader'}>
                {!isCORAPage && !isShipmentHistoryPage && (
                <KatNavigation slot='top' mode='horizontal'>
                    <div className={'nav-container'}>
                        <div className={'nav-links'}>
                            <Link to='/' className={'nav-item'}>Home</Link>
                            <Link to={granularSLALanding} className={'nav-item'}>SLA</Link>
                            <Link to={bulkGranularSLARoute} className={'nav-item'}>Bulk SLA</Link>
                            <Link to={r2fThresholdRoute} className={'nav-item'}>TRB Thresholds</Link>
                            <Link to={bulkR2fThresholdRoute} className={'nav-item'}>Bulk TRB Thresholds</Link>
                            <Link to={trbRoute} className={'nav-item'}>TRB Status</Link>
                            {( user?.isDevAdmin || user?.ppgAdmin ) && (
                                <Link to={fcConfigRoute} className={'nav-item'}>FC Config</Link>
                            )}
                            {getEnv() !== ENV.PRODUCTION && (
                                <Link to={coraRoute} className={'nav-item'}>CORA</Link>
                            )}
                            {getEnv() !== ENV.PRODUCTION && (
                                <Link to={shipmentHistoryRoute} className={'nav-item'}>Shipment History</Link>
                            )}
                        </div>

                    </div>
                </KatNavigation>
                )}
                <div className={'nav-right'}>
                    {isTRBPage && (
                        <select
                            value={currentTRBRegion}
                            onChange={( e ) => handleTRBRegionChange( e.target.value )}
                            className={'trb-header-region-select'}
                            disabled={getEnv() !== ENV.PRODUCTION}
                        >
                            {TRB_REGIONS.map( ( r ) => (
                                <option key={r} value={r}>
                                    Region: {r}
                                </option>
                            ) )}
                        </select>
                    )}
                    <KatPopover katAriaBehavior='tooltip'
                                position='bottom'
                                triggerType='click'>
                        <KatButton
                            slot='trigger'
                            variant='secondary'>
                            Welcome {user?.login}
                        </KatButton>
                        <h4>Permissions Granted:</h4>
                        {permissionsAvailable()}
                        <h4>Support Resources</h4>
                        <ul>
                            <li>
                                <KatButton variant='link' onClick={() => window.open( '/support/new' )}>
                                    Create New Ticket
                                </KatButton>
                            </li>
                            <li>
                                <KatButton variant='link' onClick={() => window.open( '/support/knowledge-base' )}>
                                    Knowledge Base
                                </KatButton>
                            </li>
                        </ul>
                    </KatPopover>
                </div>
            </div>
            {isTRBPage && (
                <div className={'trb-download-bar'}>
                    <KatButton
                        variant='primary'
                        onClick={() => window.open( snapshotUrls?.cptSnapshotUrl, '_blank', 'noopener,noreferrer' )}
                        disabled={!snapshotUrls?.cptSnapshotUrl}
                    >
                        Download FCRE Risk Snapshots
                    </KatButton>
                    <KatButton
                        variant='primary'
                        onClick={() => window.open( snapshotUrls?.r2fSnapshotUrl, '_blank', 'noopener,noreferrer' )}
                        disabled={!snapshotUrls?.r2fSnapshotUrl}
                    >
                        Download TRB Snapshots
                    </KatButton>
                </div>
            )}
            <div className={'main-content'}>
                    <Outlet />
            </div>

            <footer slot='bottom'></footer>
        </KatAppLayout>
    );
});

export default App;
